import { Box, InputAdornment, TextField, Typography } from "@mui/material";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { useTranslation } from "react-i18next";
import { CurrencySelect } from "../../components/currency_select/CurrencySelect";
import {
  calculateTapped,
  onCompoundInterestFormulaCopiedTapped,
  onCreditCardBalanceChanged,
  onCurrencyChanged,
  onInterestRateChanged,
  onMonthlyRepaymentAmountChanged,
  onResultMonthsToPayOffDebtCopiedTapped,
  onResultTotalInterestPaidCopiedTapped,
  onResultTotalPayBackCopiedTapped,
  selectState,
} from "./CreditCardRepaymentCalculatorSlice";
import Grid2 from "@mui/material/Unstable_Grid2/Grid2";
import { CustomButton } from "../../components/custom_button/CustomButton";
import { ResultCard } from "../../components/result_card/ResultCard";
import { SideMenuPageContainer } from "../../components/side_menu_page_container/SideMenuPageContainer";
import CompoundInterestCalculatorImage from "../../assets/CompoundInterestCalculator.webp";
import { HomeCard } from "../../components/home_card/HomeCard";
import AdSense from "../../components/adsense/AdSense";
import { ADSENSE_SLOTS } from "../../config/adsense";

export function CreditCardRepaymentCalculator() {
  const dispatch = useAppDispatch();
  const state = useAppSelector(selectState);
  const { t } = useTranslation();

  return (
    <SideMenuPageContainer
      pageTitle={t("creditCardCalculator.title")}
      pageSeoTitle={t("creditCardCalculator.seoTitle")}
      pageDescription={t("creditCardCalculator.description")}
    >
      <Grid2
        container
        spacing={2}
        sx={{ mt: 2 }}
        columns={{ xs: 6, sm: 6, md: 12 }}
      >
        <Grid2 xs={12}>
          <Typography variant="h2" sx={{ mb: 2 }}>
            {t("creditCardCalculator.mainHeading")}
          </Typography>
        </Grid2>

        <Grid2 xs={6}>
          <CurrencySelect
            label={t("creditCardCalculator.currencyLabel")}
            value={state.currency}
            onChange={(e) => {
              dispatch(onCurrencyChanged(e.target.value));
            }}
          />
        </Grid2>
        <Grid2 xs={6}>
          <TextField
            fullWidth
            label={t("creditCardCalculator.creditCardBalanceLabel")}
            variant="outlined"
            color="info"
            value={state.creditCardBalance}
            onChange={(e) => {
              dispatch(onCreditCardBalanceChanged(Number(e.target.value)));
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Typography>{state.currency}</Typography>
                </InputAdornment>
              ),
            }}
          />
        </Grid2>
        <Grid2 xs={6}>
          <TextField
            fullWidth
            label={t("creditCardCalculator.interestRateLabel")}
            variant="outlined"
            color="info"
            value={state.interestRatePercentage}
            onChange={(e) => {
              dispatch(onInterestRateChanged(Number(e.target.value)));
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <Typography>%</Typography>
                </InputAdornment>
              ),
            }}
          />
        </Grid2>
        <Grid2 xs={6}>
          <TextField
            fullWidth
            label={t("creditCardCalculator.monthlyRepaymentLabel")}
            variant="outlined"
            color="info"
            value={state.monthlyRepaymentAmount}
            onChange={(e) => {
              dispatch(onMonthlyRepaymentAmountChanged(Number(e.target.value)));
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Typography>{state.currency}</Typography>
                </InputAdornment>
              ),
            }}
          />
        </Grid2>
        <Grid2 xs={12}>
          <CustomButton
            label={t("creditCardCalculator.calculateButton")}
            onClick={() => {
              dispatch(calculateTapped());
            }}
          />
        </Grid2>
        <Grid2>
          <Typography
            variant={"error"}
            sx={{
              display: state.showInstallmentLowerThanCreditError
                ? "inline"
                : "none",
            }}
          >
            {t("creditCardCalculator.errorMessage")}
          </Typography>
        </Grid2>
        <Grid2 xs={12}>
          <ResultCard
            value={`${state.resultMonthsToPayOffDebt}`}
            description={t("creditCardCalculator.results.monthsToPayOff")}
            symbolAtStart={false}
            symbol="Months"
            resultCopied={state.resultMonthsToPayOffDebtCopied}
            onCopy={() => {
              dispatch(onResultMonthsToPayOffDebtCopiedTapped());
            }}
          />
        </Grid2>
        <Grid2 xs={12}>
          <ResultCard
            value={`${state.resultTotalPayBack}`}
            description={t("creditCardCalculator.results.totalPayBack")}
            symbolAtStart={true}
            symbol={state.currency}
            resultCopied={state.resultTotalPayBackCopied}
            onCopy={() => {
              dispatch(onResultTotalPayBackCopiedTapped());
            }}
          />
        </Grid2>
        <Grid2 xs={12}>
          <ResultCard
            value={`${state.resultTotalInterestPaid}`}
            description={t("creditCardCalculator.results.totalInterestPaid")}
            symbolAtStart={true}
            symbol={state.currency}
            resultCopied={state.resultTotalInterestPaidCopied}
            onCopy={() => {
              dispatch(onResultTotalInterestPaidCopiedTapped());
            }}
          />
        </Grid2>
        <Grid2 xs={12}>
          <AdSense
                adSlot={ADSENSE_SLOTS.IN_ARTICLE}
                adFormat="fluid"
                adLayoutKey="-fb+5w+4e-db+86"
                sx={{ mb: 3 }}
              />
          <Typography variant="h2" sx={{ mt: 5 }}>
            {t("creditCardCalculator.content.understandingHeading")}
          </Typography>
          <Typography variant="body1" sx={{ mt: 3, mb: 3 }}>
            {t("creditCardCalculator.content.understandingDescription")}
          </Typography>
          <ResultCard
            value={`A = P(1 + r/12)¹²ᵗ`}
            description={t(
              "creditCardCalculator.results.compoundInterestFormula"
            )}
            symbol={""}
            resultCopied={state.compoundInterestFormulaCopied}
            onCopy={() => {
              dispatch(onCompoundInterestFormulaCopiedTapped());
            }}
          />
          <Typography variant="body1" sx={{ mt: 3 }}>
            {t("creditCardCalculator.content.formulaDescription")}
          </Typography>
        </Grid2>
        <Grid2 xs={12}>
          <Typography variant="h3" sx={{ mt: 3, mb: 1 }}>
            {t("creditCardCalculator.related")}
          </Typography>
        </Grid2>
        <Grid2 xs={6}>
          <HomeCard
            languageResource="interestCalculator"
            link="/CompoundInterestCalculator"
            smallCard={true}
            image={CompoundInterestCalculatorImage}
          />
        </Grid2>
      </Grid2>
    </SideMenuPageContainer>
  );
}
